import { Graphics } from 'pixi.js';
import { type System, ComponentStore } from '@ecs/world';
import { DEFAULT_TUNING, type Tuning } from '@game/tuning';
import type { CameraResource, PlayerState, TimeScaleResource, TransformComponent } from '@game/scenes/characterPrototype/types';
import { clamp } from '@game/scenes/characterPrototype/math';

export type DashCooldownBarStyle = {
	backgroundColor: number;
	backgroundAlpha: number;
	fillColor: number;
	fillAlphaReady: number;
	fillAlphaCooldown: number;
	strokeColor: number;
	strokeAlpha: number;
	strokeWidth: number;
};

export type DashCooldownGraphicsSyncOptions = {
	style: DashCooldownBarStyle;
	yOffsetExtra: number;
	minWidth: number;
};

export function createDashCooldownGraphicsSyncSystem(
	dashGraphics: ComponentStore<Graphics>,
	states: ComponentStore<PlayerState>,
	transforms: ComponentStore<TransformComponent>,
	options: DashCooldownGraphicsSyncOptions
): System {
	return ({ world }) => {
		const tuning = world.getResource<Tuning>('tuning') ?? DEFAULT_TUNING;
		const timescale = world.getResource<TimeScaleResource>('timescale');
		const camera = world.getResource<CameraResource>('camera');
		const playerId = world.getResource<number>('player-entity');
		if (!playerId) return;
		const t = transforms.get(playerId);
		const state = states.get(playerId);
		const g = dashGraphics.get(playerId);
		if (!t || !state || !g) return;

		const offsetY = camera ? camera.y : 0;
		const alpha = timescale ? 0.85 + 0.15 * timescale.value : 1;
		const size = tuning.playerSize;
		g.position.set(t.x, t.y - offsetY - (size * 0.5 + options.yOffsetExtra));
		g.alpha = alpha;

		const cd = Math.max(0, state.dashCooldown);
		const dur = Math.max(0.05, tuning.dashCooldown);
		const progress = cd <= 0 ? 1 : clamp(1 - cd / dur, 0, 1);
		const w = Math.max(options.minWidth, size * 0.95);
		const left = -w / 2;

		g.clear();
		// background
		g.roundRect(left, -2, w, 6, 3);
		g.fill({ color: options.style.backgroundColor, alpha: options.style.backgroundAlpha });
		// fill
		g.roundRect(left, -2, w * progress, 6, 3);
		g.fill({ color: options.style.fillColor, alpha: cd <= 0 ? options.style.fillAlphaReady : options.style.fillAlphaCooldown });
		g.roundRect(left, -2, w, 6, 3);
		g.stroke({ color: options.style.strokeColor, alpha: options.style.strokeAlpha, width: options.style.strokeWidth });
	};
}


